'use client';
/**
 * Chat Connection Context (transport-agnostic)
 *
 * Provides chat connection/stream state throughout the component tree.
 * Backed by SSE in the current implementation, but intentionally generic.
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { ConnectionStatus } from './ChatConnectionTypes';

export interface ChatConnectionContextValue {
  connectionStatus: ConnectionStatus;
  isConnected: boolean;
  isConnecting: boolean;
  hasError: boolean;
  isStreaming: boolean;
  currentStream: string;
  messagesCount: number;
  reconnectAttempts: number;
  lastConnectedTime?: Date;
}

const ChatConnectionContext = createContext<ChatConnectionContextValue | undefined>(undefined);

export interface ChatConnectionProviderProps {
  children: ReactNode;
  value: ChatConnectionContextValue;
}

export const ChatConnectionProvider: React.FC<ChatConnectionProviderProps> = ({ children, value }) => {
  return (
    <ChatConnectionContext.Provider value={value}>{children}</ChatConnectionContext.Provider>
  );
};

export const useChatConnectionContext = (): ChatConnectionContextValue => {
  const context = useContext(ChatConnectionContext);
  if (context === undefined) {
    throw new Error('useChatConnectionContext must be used within a ChatConnectionProvider');
  }
  return context;
};

export const useChatTypingStatus = () => {
  const context = useChatConnectionContext();
  return {
    isTyping: context.isStreaming,
    connectionStatus: context.connectionStatus,
    hasError: context.hasError,
    isConnected: context.isConnected,
    isConnecting: context.isConnecting,
  };
};

export default ChatConnectionContext;


